import React from 'react';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import Button from '../../bootstrap/Button';
import { CardHeader, CardLabel, CardTitle, CardActions } from '../../bootstrap/Card';

interface StudentFeeSummaryTableProps {
	studentTotalFeeCollectionData: any[];
	showDetailButton?: boolean;
	onDetailClick?: () => void;
}

const StudentFeeSummaryTable: React.FC<StudentFeeSummaryTableProps> = ({
	studentTotalFeeCollectionData,
	showDetailButton = false,
	onDetailClick,
}) => {
	const navigate = useNavigate();

	const handleDetailClick = () => {
		if (onDetailClick) {
			onDetailClick();
		} else {
			navigate('/academic/studentFeeDetails');
		}
	};

	if (!studentTotalFeeCollectionData || studentTotalFeeCollectionData.length === 0) {
		return (
			<div className='text-danger col-12 d-flex justify-content-center py-4'>
				<p className='fw-bold fs-5 mb-0'>Fees Not Collected Yet.</p>
			</div>
		);
	}

	return (
		<>
			<div className='d-flex flex-wrap w-100'>
				<CardHeader className='w-100'>
					<CardLabel icon='Task' iconColor='primary'>
						<CardTitle tag='div' className='h5'>
							Fee Summary
						</CardTitle>
					</CardLabel>
					{showDetailButton && (
						<CardActions>
							<Button
								icon='Payments'
								color='primary'
								onClick={handleDetailClick}
								className='fw-semibold'>
								{onDetailClick ? 'Detail' : 'Pay / View Details'}
							</Button>
						</CardActions>
					)}
				</CardHeader>
			</div>
			<div className='table-responsive'>
				<table className='table table-modern table-hover'>
				<thead className='text-center text-uppercase align-middle'>
					<tr>
						<th className='table-info'></th>
						<th className='table-info'>
							Sem <br /> Fees
						</th>
						<th className='table-info'>
							Prev - Sem <br /> Fees
						</th>
						<th className='table-info'>
							Fine <br /> Fees
						</th>
						<th className='table-info'>
							Bus <br /> Fees
						</th>
						<th className='table-info'>
							Prev - Sem Bus <br /> Fees
						</th>
						<th className='table-info'>
							Sem - Fine <br /> Fees
						</th>
					</tr>
				</thead>

				{studentTotalFeeCollectionData.map((data: any) => (
					<tbody className='text-center align-middle' key={data.studentDetailsId}>
						<tr>
							<th>
								GROSS <br /> PAYABLE
							</th>
							<td>
								<strong>{data.semFees_grossPayable}</strong>
							</td>
							<td>
								<strong>{data.prevSemFees_grossPayable}</strong>
							</td>
							<td>
								<strong>{data.totalFineAmount}</strong>
							</td>
							<td>
								<strong>{data.busFees_grossPayable}</strong>
							</td>
							<td>
								<strong>{data.prevBusFees_grossPayable}</strong>
							</td>
							<td>
								<strong>{data.extraFees_grossPayable}</strong>
							</td>
						</tr>
						<tr>
							<th>AMOUNT PAID</th>
							<td>
								<strong>{data.semFees_amountPaid}</strong>
							</td>
							<td>
								<strong>{data.prevSemFees_amountPaid}</strong>
							</td>
							<td>
								<strong>{data.totalFineAmount}</strong>
							</td>
							<td>
								<strong>{data.busFees_amountPaid}</strong>
							</td>
							<td>
								<strong>{data.prevBusFees_amountPaid}</strong>
							</td>
							<td>
								<strong>{data.extraFees_amountPaid}</strong>
							</td>
						</tr>
						<tr>
							<th>CONCESSION</th>
							<td>
								<strong>{data.semFees_concession}</strong>
							</td>
							<td>
								<strong>{data.prevSemFees_concession}</strong>
							</td>
							<td>
								<strong>0</strong>
							</td>
							<td>
								<strong>{data.busFees_concession}</strong>
							</td>
							<td>
								<strong>{data.prevBusFees_concession}</strong>
							</td>
							<td>
								<strong>{data.extraFees_concession}</strong>
							</td>
						</tr>
						<tr>
							<th>NET PAYABLE</th>
							<td>
								{data.semFees_netPayable === 0 ? (
									<strong className='text-success'>{data.semFees_netPayable}</strong>
								) : (
									<strong className='text-danger'>{data.semFees_netPayable}</strong>
								)}
							</td>
							<td>
								{data.prevSemFees_netPayable === 0 ? (
									<strong className='text-success'>{data.prevSemFees_netPayable}</strong>
								) : (
									<strong className='text-danger'>{data.prevSemFees_netPayable}</strong>
								)}
							</td>

							<td>
								<strong>0</strong>
							</td>

							<td>
								{data.busFees_netPayable === 0 ? (
									<strong className='text-success'>{data.busFees_netPayable}</strong>
								) : (
									<strong className='text-danger'>{data.busFees_netPayable}</strong>
								)}
							</td>
							<td>
								{data.prevBusFees_netPayable === 0 ? (
									<strong className='text-success'>{data.prevBusFees_netPayable}</strong>
								) : (
									<strong className='text-danger'>{data.prevBusFees_netPayable}</strong>
								)}
							</td>
							<td>
								{data.extraFees_netPayable === 0 ? (
									<strong className='text-success'>{data.extraFees_netPayable}</strong>
								) : (
									<strong className='text-danger'>{data.extraFees_netPayable}</strong>
								)}
							</td>
						</tr>
					</tbody>
				))}
			</table>
			<br />

			{studentTotalFeeCollectionData.map((data: any) => {
				const totalBalance =
					data.semFees_netPayable +
					data.prevSemFees_netPayable +
					data.busFees_netPayable +
					data.prevBusFees_netPayable +
					data.extraFees_netPayable;

				return (
					<div className='row align-items-center' key={data.studentDetailsId}>
						<div className='col'>
						</div>
						<div className='col-auto d-flex justify-content-end align-items-center'>
							<span className='fw-bold fs-5 mb-0'> Balance : </span>
							<strong className={classNames('fs-5 mb-0 ms-2', totalBalance === 0 ? 'text-success' : 'text-danger')}>
								₹ {totalBalance}
							</strong>
						</div>
					</div>
				);
			})}
		</div>
		</>
	);
};

export default StudentFeeSummaryTable;
